/**
 * Parses a PESEL number and extracts birth date and gender information.
 *
 * PESEL is an 11-digit Polish national identification number.
 * Format: YYMMDDXXXXX where:
 * - YY: last two digits of birth year
 * - MM: birth month (with century encoding)
 * - DD: birth day
 * - XXXXX: additional digits including sex identifier and checksum
 *
 * Gender is determined by the 10th digit (index 9):
 * - Even digits (0,2,4,6,8): Female
 * - Odd digits (1,3,5,7,9): Male
 *
 * @param input - The PESEL number to parse (may contain whitespace)
 * @returns Parsed PESEL data with normalized number, birth date, and gender, or null if invalid
 * @util
 * @__NO_SIDE_EFFECTS__
 */
export function parsePesel(input) {
    // Normalize input by trimming whitespace
    const pesel = input.trim();
    // Validate basic format - must be exactly 11 digits
    if (!/^\d{11}$/.test(pesel)) {
        return null;
    }
    // Extract date components
    const year = Number.parseInt(pesel.slice(0, 2), 10);
    const month = Number.parseInt(pesel.slice(2, 4), 10);
    const day = Number.parseInt(pesel.slice(4, 6), 10);
    // Determine century and actual year based on month encoding
    let actualYear;
    let actualMonth;
    if (month >= 1 && month <= 12) {
        // 1900-1999
        actualYear = 1900 + year;
        actualMonth = month;
    }
    else if (month >= 21 && month <= 32) {
        // 2000-2099
        actualYear = 2000 + year;
        actualMonth = month - 20;
    }
    else if (month >= 41 && month <= 52) {
        // 2100-2199
        actualYear = 2100 + year;
        actualMonth = month - 40;
    }
    else if (month >= 61 && month <= 72) {
        // 2200-2299
        actualYear = 2200 + year;
        actualMonth = month - 60;
    }
    else if (month >= 81 && month <= 92) {
        // 1800-1899
        actualYear = 1800 + year;
        actualMonth = month - 80;
    }
    else {
        // Invalid month encoding
        return null;
    }
    // Validate day
    if (day < 1 || day > 31) {
        return null;
    }
    // Create date and validate it exists (handles invalid dates like Feb 30)
    const birthDate = new Date(actualYear, actualMonth - 1, day);
    if (birthDate.getFullYear() !== actualYear ||
        birthDate.getMonth() !== actualMonth - 1 ||
        birthDate.getDate() !== day) {
        return null;
    }
    // Extract gender from 10th digit (index 9)
    const genderDigit = Number.parseInt(pesel[9], 10);
    const gender = genderDigit % 2 === 0 ? 'FEMALE' : 'MALE'; // FEMALE : MALE
    // Validate checksum
    const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
    let sum = 0;
    for (let i = 0; i < 10; i++) {
        sum += Number.parseInt(pesel[i], 10) * weights[i];
    }
    const checksum = (10 - (sum % 10)) % 10;
    const providedChecksum = Number.parseInt(pesel[10], 10);
    if (checksum !== providedChecksum) {
        return null;
    }
    return {
        pesel,
        birthDate,
        gender,
    };
}
//# sourceMappingURL=parsePesel.js.map